import { useState } from "react";
import { DeviationDecisionDto, InspectionDeviationResultDto, InspectionDto } from "@/types";
import { NumberValue, toNumber } from "@/components/organisms/InspectionModal/useInspectionForm";
import { PER_ORDER_UNIT_CODE } from "@/components/organisms/InspectionModal/utils";

export type DecisionChoice = DeviationDecisionDto["decision"];

/** A manager's decision on one pending deviation, before it's saved. */
export interface DecisionValues {
  /** Null until the manager picks one; undecided deviations aren't sent. */
  decision: DecisionChoice | null;
  /** The weight in kg for a product, the registered number for a service. Starts at the inspector's value. */
  amount: NumberValue;
  /** Required when rejecting. */
  comment: string;
}

/** Validation errors as translation keys, keyed by deviation id and field (e.g. "45.comment"). */
export type DecisionErrors = Record<string, string>;

// The backend's limit (POST /app/api/inspections/orders/{orderId}/deviation-decisions).
export const MAX_DECISION_COMMENT_LENGTH = 2000;

export const isPendingDeviation = (deviation: InspectionDeviationResultDto) =>
  deviation.confirmationStatus === "PENDING";

/** What the inspector registered: a product's weight in kg, a service's number. */
export const registeredAmount = (deviation: InspectionDeviationResultDto) =>
  deviation.kind === "PRODUCT" ? (deviation.weightKg ?? deviation.registeredNumber) : deviation.registeredNumber;

/** A confirmation with another weight or number than the inspector's, which the backend reprices on save. */
const isAdjusted = (deviation: InspectionDeviationResultDto, values: DecisionValues) =>
  values.decision === "CONFIRMED" && toNumber(values.amount) !== registeredAmount(deviation);

const initialValues = (deviation: InspectionDeviationResultDto): DecisionValues => ({
  decision: null,
  amount: registeredAmount(deviation),
  comment: "",
});

/**
 * The confirmation form: a decision per pending deviation, saved together. Only what the manager changed is kept;
 * the rest follows the inspection, so deviations someone else decided meanwhile drop out of the form on reload.
 */
export const useDeviationDecisionsForm = (inspection: InspectionDto | undefined) => {
  const [changes, setChanges] = useState<Record<number, DecisionValues>>({});

  const pending = (inspection?.deviations ?? []).filter(isPendingDeviation);
  const valuesOf = (deviation: InspectionDeviationResultDto) => changes[deviation.id] ?? initialValues(deviation);
  const decided = pending.filter((deviation) => valuesOf(deviation).decision !== null);

  const update = (deviation: InspectionDeviationResultDto, patch: Partial<DecisionValues>) =>
    setChanges((current) => ({
      ...current,
      [deviation.id]: { ...(current[deviation.id] ?? initialValues(deviation)), ...patch },
    }));

  /** Confirms every pending deviation that has no decision yet, as registered; rejections stay. */
  const confirmAll = () =>
    setChanges((current) => {
      const next = { ...current };
      pending.forEach((deviation) => {
        const values = current[deviation.id] ?? initialValues(deviation);
        if (values.decision === null) next[deviation.id] = { ...values, decision: "CONFIRMED" };
      });
      return next;
    });

  return {
    pending,
    valuesOf,
    update,
    confirmAll,
    hasUndecided: decided.length < pending.length,
    decisions: decided.map((deviation) => toDecision(deviation, valuesOf(deviation))),
    errors: validateDecisions(pending, valuesOf),
    /** Forgets the decisions, after they're saved. */
    reset: () => setChanges({}),
  };
};

export type DeviationDecisionsForm = ReturnType<typeof useDeviationDecisionsForm>;

/** Sends an adjustment only when the value changed: left out, the backend keeps the inspector's value. */
const toDecision = (deviation: InspectionDeviationResultDto, values: DecisionValues): DeviationDecisionDto => {
  if (values.decision === "REJECTED") {
    return { deviationId: deviation.id, decision: "REJECTED", comment: values.comment.trim() };
  }
  const amount = toNumber(values.amount);
  const adjustment = !isAdjusted(deviation, values)
    ? {}
    : deviation.kind === "PRODUCT"
      ? { weightKg: amount }
      : { registeredNumber: amount };
  return { deviationId: deviation.id, decision: "CONFIRMED", ...adjustment };
};

/**
 * The checks the form can make on its own; the messages are translation keys. The rest, such as products weighing
 * more than the delivery, comes back from the backend's decision preview with its reason.
 */
const validateDecisions = (
  pending: InspectionDeviationResultDto[],
  valuesOf: (deviation: InspectionDeviationResultDto) => DecisionValues
): DecisionErrors => {
  const errors: DecisionErrors = {};
  pending.forEach((deviation) => {
    const values = valuesOf(deviation);
    const key = (field: keyof DecisionValues) => `${deviation.id}.${field}`;

    if (values.decision === "REJECTED" && !values.comment.trim()) {
      errors[key("comment")] = "validation:inspection.rejectCommentRequired";
    }
    // A "per order" service is counted once, so it has no number to adjust.
    const hasAmount = deviation.kind === "PRODUCT" || deviation.unitCode !== PER_ORDER_UNIT_CODE;
    if (values.decision === "CONFIRMED" && hasAmount && !(toNumber(values.amount) > 0)) {
      errors[key("amount")] =
        deviation.kind === "PRODUCT"
          ? "validation:inspection.deviationWeightRequired"
          : "validation:inspection.registeredNumberRequired";
    }
  });
  return errors;
};
